import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import * as Sentry from "@sentry/react";
import { logger } from '@/lib/logger';
import { configureGoogleAdsTag, initCookieConsent } from '@/lib/cookieConsent';
import { captureAdClickAttribution } from '@/lib/gclidCapture';
import { GOOGLE_ADS_ID, assertGa4Matches } from '@/config/analytics';

// Detect environment based on hostname
function detectEnvironment(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  if (hostname.includes('staging') || hostname.includes('form-haven-backup-hub-staging')) {
    return 'staging';
  }
  if (hostname.includes('form-dev') || hostname.includes('.pages.dev')) {
    return 'preview';
  }
  if (hostname === 'fab.day' || hostname === 'www.fab.day') {
    return 'production';
  }
  return 'unknown';
}

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  const environment = detectEnvironment();
  const currentUrl = window.location.href;
  const hostname = window.location.hostname;

  // Only enable replays in production to stay within free plan limits
  const isProduction = environment === 'production';
  const integrations = isProduction
    ? [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Security: Mask sensitive data in session replays
          maskAllText: true, // Mask all text content to prevent credential leakage
          maskAllInputs: true, // Mask all input fields (passwords, OAuth codes, etc.)
          blockAllMedia: true, // Block media that could contain customer data
          // Additional privacy controls
          block: ['[data-sensitive]'], // Block elements marked as sensitive
        }),
      ]
    : [Sentry.browserTracingIntegration()];

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: environment, // Tag environment for filtering
    integrations: integrations,
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/], // Adjust as needed
    // Session Replay - only enabled in production
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
    // Add initial tags for easy filtering
    initialScope: {
      tags: {
        hostname: hostname,
        environment: environment,
      },
      contexts: {
        app_info: {
          current_url: currentUrl,
          origin: window.location.origin,
        }
      }
    }
  });

  // Log environment detection
  logger.debug(`[Sentry] Initialized for environment: ${environment} (${hostname})`);
  logger.debug(`[Sentry] Replays enabled: ${isProduction}`);
  logger.debug(`[Sentry] Current URL: ${currentUrl}`);
} else {
  logger.warn("SENTRY_DSN not found. Sentry will not be initialized.");
}

// Gate the Ads tag on the production hostname so dev/staging/preview builds
// can't leak conversions into the prod Ads account.
if (detectEnvironment() === 'production') {
  configureGoogleAdsTag(GOOGLE_ADS_ID);
}
initCookieConsent();
assertGa4Matches();
// Attempt gclid capture on first paint. No-op if advertising consent is
// not yet granted; cookieConsent.ts retries once consent flips to granted.
captureAdClickAttribution();

createRoot(document.getElementById("root")!).render(<App />);
